import {
  ComponentInternalInstance,
  getCurrentInstance as getCurrentInstanceOriginal,
} from '@vue/composition-api';
import invariant from 'invariant';

import PREDEFINED_STATUS_CODE_MESSAGES from './data/error-messages';

import { SObject, AppServerError } from './types';

/**
 * Verifies that passed value is defined. Intended to use as a TS guard
 * @example
 * const values = [1, 2, undefined, 3, null].filter(isDefined) // typeof values => number[]
 */
export function isDefined<T>(x: T | undefined | null): x is T {
  return x !== undefined && x !== null;
}

export function getCurrentInstance(): ComponentInternalInstance {
  const instance = getCurrentInstanceOriginal();
  invariant(instance !== null, '');
  return instance;
}

const DEFAULT_ERROR_MESSAGE = 'Something went wrong';
export function errorToMessage(error: AppServerError): string {
  const { response } = error;
  if (response !== undefined) {
    return (
      response.data.message ||
      PREDEFINED_STATUS_CODE_MESSAGES[response.status] ||
      DEFAULT_ERROR_MESSAGE
    );
  }
  return DEFAULT_ERROR_MESSAGE;
}

export function isSObject(val: unknown): val is SObject {
  return Object.values(SObject).includes(val as SObject);
}

/** Immutable version of `Array#slice` */
export function immutableSlice<T>(
  array: T[],
  index: number,
  itemToReplace: T
): T[] {
  return [...array.slice(0, index), itemToReplace, ...array.slice(index + 1)];
}

export function indexArray<T extends string>(items: T[]): Record<string, T> {
  return items.reduce((acc, curr) => {
    acc[curr] = curr;
    return acc;
  }, {} as Record<string, T>);
}

export function indexBy<
  K extends string | number | symbol,
  V extends string | number | symbol,
  T extends Record<K, V>
>(items: T[], fieldName: K): Record<T[K], T> {
  return items.reduce((acc, curr) => {
    acc[curr[fieldName]] = curr;
    return acc;
  }, {} as Record<T[K], T>);
}
